import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import {
  BookOpen, GraduationCap, Globe, Mic, Shield, Users,
  MessageSquare, AlertTriangle, Trophy, Lock, CheckCircle,
  ChevronRight, Star, Zap, Target, Award, Sparkles,
  ArrowRight, Play, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Öğrenme Yolu Tanımları
const LEARNING_PATH = {
  stages: [
    {
      id: 'beginner',
      name: 'Başlangıç',
      level: 'A1-A2',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-300',
      icon: '🌱',
      requiredCompletion: 70,
      modules: [
        {
          id: 'vocabulary',
          title: 'Temel Havacılık Kelimeleri',
          description: 'Havacılık terminolojisinin temeli',
          icon: BookOpen,
          path: '/cabin-crew/flashcards',
          estimatedTime: '2-3 saat',
          milesReward: 150
        },
        {
          id: 'basic-grammar',
          title: 'Temel Gramer',
          description: 'İngilizce gramer temelleri',
          icon: GraduationCap,
          path: '/languages',
          estimatedTime: '3-4 saat',
          milesReward: 200
        },
        {
          id: 'icao',
          title: 'ICAO Alfabesi',
          description: 'Uluslararası havacılık alfabesi',
          icon: Globe,
          path: '/cabin-crew/timed-challenge',
          estimatedTime: '1-2 saat',
          milesReward: 100
        }
      ]
    },
    {
      id: 'intermediate',
      name: 'Orta Seviye',
      level: 'B1-B2',
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-300',
      icon: '📚',
      requiredCompletion: 70,
      modules: [
        {
          id: 'dialogues',
          title: 'Diyaloglar',
          description: 'Yolcu ile iletişim kalıpları',
          icon: MessageSquare,
          path: '/cabin-crew/roleplay',
          estimatedTime: '3-4 saat',
          milesReward: 200
        },
        {
          id: 'announcements',
          title: 'Anonslar (PA)',
          description: 'Uçuş içi anons pratikleri',
          icon: Mic,
          path: '/cabin-crew/voice-practice',
          estimatedTime: '2-3 saat',
          milesReward: 180
        },
        {
          id: 'advanced-grammar',
          title: 'İleri Gramer',
          description: 'Karmaşık cümle yapıları',
          icon: GraduationCap,
          path: '/exercises',
          estimatedTime: '4-5 saat',
          milesReward: 250
        }
      ]
    },
    {
      id: 'advanced',
      name: 'İleri Seviye',
      level: 'C1-C2',
      color: 'from-red-500 to-rose-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-300',
      icon: '🎖️',
      requiredCompletion: 70,
      modules: [
        {
          id: 'emergency',
          title: 'Acil Durumlar',
          description: 'Güvenlik prosedürleri İngilizcesi',
          icon: AlertTriangle,
          path: '/cabin-crew/emergency',
          estimatedTime: '3-4 saat',
          milesReward: 300
        },
        {
          id: 'interview',
          title: 'Mülakat Hazırlığı',
          description: 'İş görüşmesi simülasyonu',
          icon: Users,
          path: '/cabin-crew/interview',
          estimatedTime: '4-5 saat',
          milesReward: 350
        },
        {
          id: 'roleplay-advanced',
          title: 'Rol-Play Senaryoları',
          description: 'Gerçek uçuş senaryoları',
          icon: MessageSquare,
          path: '/cabin-crew/roleplay',
          estimatedTime: '3-4 saat',
          milesReward: 280
        },
        {
          id: 'final-exam',
          title: 'Final Sertifika Sınavı',
          description: 'THK onaylı değerlendirme',
          icon: Trophy,
          path: '/exam/english',
          estimatedTime: '1-2 saat',
          milesReward: 500
        }
      ]
    }
  ]
};

export default function CabinCrewRoadmap() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [roadmapProgress, setRoadmapProgress] = useState({
    currentStage: 'beginner',
    moduleProgress: {},
    unlockedStages: ['beginner'],
    totalMiles: 0
  });

  useEffect(() => {
    loadRoadmapProgress();
  }, []);

  const loadRoadmapProgress = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cabin-crew/roadmap-progress`);
      setRoadmapProgress(response.data);
    } catch (error) {
      console.error('Roadmap progress load error:', error);
      // Default progress if API fails
      setRoadmapProgress({
        currentStage: 'beginner',
        moduleProgress: {},
        unlockedStages: ['beginner'],
        totalMiles: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const isStageUnlocked = (stageId) => {
    return roadmapProgress.unlockedStages?.includes(stageId);
  };

  const getModuleProgress = (moduleId) => {
    return roadmapProgress.moduleProgress?.[moduleId] || 0;
  };

  const isModuleCompleted = (moduleId) => {
    return getModuleProgress(moduleId) >= 70;
  };

  const getStageCompletion = (stage) => {
    const completedModules = stage.modules.filter(m => isModuleCompleted(m.id)).length;
    return Math.round((completedModules / stage.modules.length) * 100);
  };

  const handleModuleClick = (module, stage) => {
    if (!isStageUnlocked(stage.id)) {
      toast.error(`Bu modüle erişmek için önce ${getPreviousStageName(stage.id)} aşamasını tamamlayın!`);
      return;
    }
    navigate(module.path);
  };

  const getPreviousStageName = (stageId) => {
    const stageIndex = LEARNING_PATH.stages.findIndex(s => s.id === stageId);
    if (stageIndex > 0) {
      return LEARNING_PATH.stages[stageIndex - 1].name;
    }
    return '';
  };

  const getCurrentStageIndex = () => {
    return LEARNING_PATH.stages.findIndex(s => s.id === roadmapProgress.currentStage);
  };

  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Yol haritası yükleniyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }

  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-700 text-white py-8">
          <div className="container mx-auto px-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Target className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-black">Öğrenme Yol Haritası</h1>
                <p className="text-sky-100">Kabin memurluğuna adım adım ilerle</p>
              </div>
            </div>

            {/* Overall Progress */}
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-300" />
                    <span className="font-semibold">Toplam Mil: {roadmapProgress.totalMiles}</span>
                  </div>
                  <Badge className="bg-white/20">
                    Aşama {getCurrentStageIndex() + 1}/3
                  </Badge>
                </div>
                
                {/* Stage Progress Bar */}
                <div className="flex items-center gap-2">
                  {LEARNING_PATH.stages.map((stage, idx) => (
                    <React.Fragment key={stage.id}>
                      <div className="flex-1">
                        <div className="h-3 rounded-full bg-white/20 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              isStageUnlocked(stage.id)
                                ? `bg-gradient-to-r ${stage.color}`
                                : 'bg-white/10'
                            }`}
                            style={{ width: `${isStageUnlocked(stage.id) ? getStageCompletion(stage) : 0}%` }}
                          />
                        </div>
                        <p className="text-xs text-center mt-1 text-sky-100">{stage.name}</p>
                      </div>
                      {idx < LEARNING_PATH.stages.length - 1 && (
                        <ChevronRight className="w-4 h-4 text-sky-200" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Roadmap Content */}
        <div className="container mx-auto px-6 py-8">
          {/* Learning Path Timeline */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-green-500 via-amber-500 to-red-500 rounded-full hidden md:block" />

            {LEARNING_PATH.stages.map((stage, stageIndex) => {
              const isUnlocked = isStageUnlocked(stage.id);
              const stageCompletion = getStageCompletion(stage);
              const isCompleted = stageCompletion >= 70;

              return (
                <div key={stage.id} className="mb-12 relative">
                  {/* Stage Header */}
                  <div className="flex items-start gap-4 mb-6">
                    {/* Stage Icon */}
                    <div className={`relative z-10 w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-lg ${
                      isUnlocked
                        ? `bg-gradient-to-br ${stage.color} text-white`
                        : 'bg-gray-200 text-gray-400'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="w-8 h-8" />
                      ) : isUnlocked ? (
                        stage.icon
                      ) : (
                        <Lock className="w-6 h-6" />
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h2 className={`text-2xl font-black ${
                          isUnlocked ? 'text-gray-800' : 'text-gray-400'
                        }`}>
                          {stage.name}
                        </h2>
                        <Badge className={`${
                          isUnlocked
                            ? `bg-gradient-to-r ${stage.color} text-white`
                            : 'bg-gray-200 text-gray-500'
                        }`}>
                          {stage.level}
                        </Badge>
                        {isCompleted && (
                          <Badge className="bg-green-500 text-white">
                            <CheckCircle className="w-3 h-3 mr-1" /> Tamamlandı
                          </Badge>
                        )}
                      </div>
                      <p className="text-gray-500 text-sm mb-2">
                        {stage.modules.length} modül • %{stage.requiredCompletion} tamamlama gerekli
                      </p>
                      {isUnlocked && (
                        <div className="flex items-center gap-2">
                          <Progress value={stageCompletion} className="h-2 flex-1 max-w-xs" />
                          <span className="text-sm font-medium text-gray-600">%{stageCompletion}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Modules Grid */}
                  <div className="ml-0 md:ml-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {stage.modules.map((module, moduleIndex) => {
                        const Icon = module.icon;
                        const moduleProgress = getModuleProgress(module.id);
                        const moduleCompleted = isModuleCompleted(module.id);

                        return (
                          <Card
                            key={module.id}
                            className={`relative overflow-hidden transition-all cursor-pointer ${
                              isUnlocked
                                ? `hover:shadow-lg hover:-translate-y-1 ${stage.bgColor} border-2 ${stage.borderColor}`
                                : 'bg-gray-100 opacity-60 cursor-not-allowed'
                            }`}
                            onClick={() => handleModuleClick(module, stage)}
                          >
                            {/* Module Number Badge */}
                            <div className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              moduleCompleted
                                ? 'bg-green-500 text-white'
                                : isUnlocked
                                ? `bg-gradient-to-br ${stage.color} text-white`
                                : 'bg-gray-300 text-gray-500'
                            }`}>
                              {moduleCompleted ? (
                                <CheckCircle className="w-4 h-4" />
                              ) : (
                                stageIndex * 3 + moduleIndex + 1
                              )}
                            </div>

                            <CardContent className="p-5">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                                isUnlocked
                                  ? `bg-gradient-to-br ${stage.color}`
                                  : 'bg-gray-300'
                              }`}>
                                {isUnlocked ? (
                                  <Icon className="w-6 h-6 text-white" />
                                ) : (
                                  <Lock className="w-5 h-5 text-gray-500" />
                                )}
                              </div>

                              <h3 className={`font-bold mb-1 ${
                                isUnlocked ? 'text-gray-800' : 'text-gray-400'
                              }`}>
                                {module.title}
                              </h3>
                              <p className="text-sm text-gray-500 mb-3">
                                {module.description}
                              </p>

                              <div className="flex items-center justify-between text-xs text-gray-400">
                                <span>⏱️ {module.estimatedTime}</span>
                                <span>⭐ {module.milesReward} Mil</span>
                              </div>

                              {isUnlocked && (
                                <div className="mt-3">
                                  <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="text-gray-500">İlerleme</span>
                                    <span className="font-medium">%{moduleProgress}</span>
                                  </div>
                                  <Progress value={moduleProgress} className="h-1.5" />
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Stage Completion Info */}
                    {isUnlocked && stageIndex < LEARNING_PATH.stages.length - 1 && !isCompleted && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 text-blue-700">
                          <Info className="w-4 h-4" />
                          <span className="text-sm">
                            Bu aşamayı %{stage.requiredCompletion} tamamlayarak <strong>{LEARNING_PATH.stages[stageIndex + 1].name}</strong> seviyesinin kilidini açın!
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="mt-8 text-center">
            <Card className="bg-gradient-to-r from-sky-500 to-indigo-600 text-white border-0">
              <CardContent className="p-8">
                <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-80" />
                <h3 className="text-2xl font-bold mb-2">Haydi Başlayalım!</h3>
                <p className="text-sky-100 mb-4">
                  Her gün biraz pratik yaparak kabin memurluğu hayalini gerçekleştir.
                </p>
                <Button
                  size="lg"
                  className="bg-white text-sky-600 hover:bg-sky-50"
                  onClick={() => navigate('/dashboard')}
                >
                  Eğitime Dön <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </CabinCrewLayout>
  );
}
